import React, {useEffect, useMemo, useState} from 'react';
import {StyleSheet, TextStyle, View, ViewStyle} from 'react-native';
import {useToast} from 'hooks';
import {
  resetMessage,
  registerOrganizationRequestPayload,
  registerOrganizationRequest,
  resendRegisterOrganizationOTPRequest,
  verifyRegisterOrganizationOTPRequest,
  verifyRegisterOrganizationOTPRequestPayload,
} from 'redux/slices/authSlice';
import {
  getAuthSuccess,
  getAuthError,
  otpTimer,
  otpAttemps,
} from 'redux/selectors/authSelector';
import {useDispatch, useSelector} from 'react-redux';
import {ERROR_KEY, SUCCESS_KEY} from 'constants/keys';
import {
  ErrorCodes,
  getErrorMessage,
  getSuccessMessage,
  SuccessCodes,
} from 'utils/getMessages';
import {
  RegisterOrginizationForm,
  GradientWithLogo,
  OtpForm,
  initialOTPValueType,
} from 'components';
import {Formik, FormikProps} from 'formik';
import {OTPValidation} from 'utils/schema';
import {Text} from 'ui-kits';
import colors from 'theme/Colors';
import {corner_radius, spacing} from 'theme/Units';
import {resetOTPTimer, toastDuration} from 'constants/DefaultValues';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {RootNavigatorParams, Routes} from 'navigation/Routes';
import { useResponsiveStyles } from 'hooks/useResponsiveStyles';

const initalValues: registerOrganizationRequestPayload = {
  org_name: '',
  first_name: '',
  last_name: '',
  email: '',
};

const initialOTPValue: initialOTPValueType = {
  otp: '',
};

export function RegisterOrg({
  navigation,
}: NativeStackScreenProps<RootNavigatorParams>) {
  const dispatch = useDispatch();
  const registerOrganizationFailure = useSelector(getAuthError) as ErrorCodes;
  const [email, setEmail] = useState<string>('');
  const registerOrganizationSuccess = useSelector(
    getAuthSuccess,
  ) as SuccessCodes;
  const [otpSent, setOtpSent] = useState<boolean>(false);

  const {showToasts} = useToast();
  const styles = useResponsiveStyles<styleType>(baseStyles, responsive);

  // handle toast message
  useEffect(() => {
    const handleFailure = () => {
      const isAccountBlocked = registerOrganizationFailure === 'E-10109';
      showToasts(
        isAccountBlocked ? 'warning' : ERROR_KEY,
        getErrorMessage(registerOrganizationFailure),
        isAccountBlocked ? 'warning' : 'error',
        isAccountBlocked ? 10 : toastDuration,
      );
      dispatch(resetMessage());
      if (isAccountBlocked){
        setOtpSent(false);
      }
    };
    const handleSuccess = () => {
      const successKey =
        registerOrganizationSuccess === 'S-10080'
          ? 'S-10012'
          : registerOrganizationSuccess;
      showToasts(SUCCESS_KEY, getSuccessMessage(successKey), 'success');

      if (registerOrganizationSuccess === 'S-10080') {
        navigation.navigate(Routes.HOME);
      } else if (registerOrganizationSuccess === 'S-10079') {
        setOtpSent(true);
      }
      dispatch(resetMessage());
    };

    if (registerOrganizationFailure) {
      handleFailure();
    }
    if (registerOrganizationSuccess){
      handleSuccess();
    }
  }, [
    registerOrganizationFailure,
    registerOrganizationSuccess,
    showToasts,
    dispatch,
    navigation,
  ]);

  function handleSubmit(values: registerOrganizationRequestPayload) {
    setEmail(values.email);
    dispatch(registerOrganizationRequest(values));
  }

  function handleOTPSubmit(value: verifyRegisterOrganizationOTPRequestPayload) {
    dispatch(verifyRegisterOrganizationOTPRequest(value));
  }

  function navigateToHome(){
    navigation.navigate(Routes.HOME);
  }

  return (
    <View style={styles.container}>
      <GradientWithLogo />
      <View style={styles.formContainer}>
        <View style={styles.form}>

        {otpSent && email ? (
          <OTPForm
          email={email}
            handleOTPSubmit={({otp}) => handleOTPSubmit({email, otp})}
          />
        ) : (
          <RegisterOrginizationForm
            initialValue={initalValues}
            onSubmit={handleSubmit}
            navigateToHome={navigateToHome}
            />
          )}
        </View>
      </View>
    </View>
  );
}

type OTPFormType = Readonly<{
  handleOTPSubmit: (value: initialOTPValueType) => void;
  email: string;
}>;

function OTPForm(props: OTPFormType) {
  const {handleOTPSubmit, email} = props;
  const otpLastUpdate = useSelector(otpTimer);
  const attemptsLeft = useSelector(otpAttemps);
  const [remainingTime, setRemainingTime] = useState<number>(0);

  const dispatch = useDispatch();

  const canResendOTP = useMemo(
    () => Boolean(remainingTime <= 0),
    [remainingTime],
  );

  function handleResendOTP() {
    dispatch(resendRegisterOrganizationOTPRequest({email}));
  }

  useEffect(() => {
    const updateTimer = () => {
      if (otpLastUpdate) {
        const timeElapsed = Math.floor(
          (Date.now() - new Date(otpLastUpdate).getTime()) / 1000,
        );
        const newRemainingTime = Math.max(resetOTPTimer - timeElapsed, 0);
        setRemainingTime(newRemainingTime);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [otpLastUpdate]);

  const styles = useResponsiveStyles(baseStyles, responsive);

  return (
    <View style={styles.otpForm}>
      <Text variant="inter18" style={styles.otpHeading}>
        OTP Verification
      </Text>
      <Formik
        initialValues={initialOTPValue}
        onSubmit={handleOTPSubmit}
        validationSchema={OTPValidation}>
        {(formikProps: FormikProps<initialOTPValueType>) => {
          return (
            <OtpForm
              label="Enter 6-digit OTP"
              buttonText="Submit"
              attemptsLeft={attemptsLeft}
              canResendOTP={canResendOTP}
              remainingTime={remainingTime}
              handleResendOTP={handleResendOTP}
              {...formikProps}
            />
          );
        }}
      </Formik>
    </View>
  );
}

type styleType = {
  container: ViewStyle;
  formContainer: ViewStyle;
  otpForm: ViewStyle;
  otpHeading: TextStyle;
  form?: ViewStyle;
  homeLink: TextStyle;
};
const baseStyles: styleType = StyleSheet.create({
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    flex: 1,
  },
  homeLink: {
    textAlign: 'center',
    color: colors.palette.blueInfo,
  },
  formContainer: {
    backgroundColor: 'white',
    justifyContent: 'center',
    padding: spacing['4'],
  },
  otpForm: {
    gap: spacing['4'],
    padding: spacing['4'],
    borderRadius: corner_radius['rounded-lg'],
    alignItems: 'center',
    minWidth: 0,
    width: '100%',
    maxWidth: 400,
    alignSelf: 'center',
    backgroundColor: colors.palette.white,
    shadowColor: 'rgb(23, 24, 28)',
    shadowOpacity: 0.1,
    shadowOffset: {height: 2, width: 0},
    shadowRadius: 4,
  },
  otpHeading: {
    color: colors.palette.brandBlue,
    fontSize: 44,
    textWrap: 'nowrap',
    fontWeight: '600',
  },
});

const responsive: Record<number, Partial<styleType>> = {
  640:{
    container: {
      gridTemplateRows: '1fr 1fr',
      gridTemplateColumns: 'none',
    },
    form: {
      transform: 'translate(0px, -15%)',
    },
    otpHeading: {
      fontSize: 40,
    },
  },
  480:{
    form: {
      transform: 'translate(0px, -18%)',
    },
    otpHeading: {
      fontSize: 36,
    },
  },
};
